import { useEffect, useLayoutEffect, useRef } from 'react'
import type { Section } from '../../../lib/db/models'
import type { SectionDropAnim } from './useSectionDrag'
import { dropPopMotion } from '../../../lib/motion/dropPopMotion'

interface RenderSegment {
  section: Section | null
  tasks: unknown[]
  taskCount: number
}

interface UseSectionDragAnimationParams {
  wrapperRef: React.RefObject<HTMLDivElement | null>
  activeSectionDragId: string | null
  hoverTargetIdx: number | null
  segments: RenderSegment[]
  dropAnimRef: React.MutableRefObject<SectionDropAnim | null>
  segmentRectsRef: React.MutableRefObject<Map<number, DOMRect>>
  sourceHeaderHeightRef: React.MutableRefObject<number>
}

/** Gap between a section and a drop zone — matches section-header margin-top. */
const SECTION_GAP = 32
/** Drop zone height during section drag. */
const DZ_HEIGHT = 32

/**
 * Handles all section-drag visual animations via direct DOM manipulation:
 *
 * A) Counter-transforms on drag start — segments and drop zones visually stay
 *    at original positions despite CSS layout changes (drop zones expand,
 *    margins collapse, source collapses).
 *
 * B) Swap transforms during drag — non-dragged segments and their drop zones
 *    animate to make room when the pointer hovers different drop zones.
 *    All positions are computed algebraically from Phase A snapshots — no
 *    getBoundingClientRect() calls, so CSS transition timing cannot cause drift.
 *
 * C) FLIP settle on drop — the dropped segment animates from the overlay's last
 *    position to its final DOM position (magnetic settle), with the same pop as a
 *    dropped task (`dropPopMotion`, #238) — also when it lands where it was. Its
 *    tasks travel the same distance and pop at the same time, each around its own
 *    centre, like a dropped stack.
 */
export function useSectionDragAnimation({
  wrapperRef,
  activeSectionDragId,
  hoverTargetIdx,
  segments,
  dropAnimRef,
  segmentRectsRef,
  sourceHeaderHeightRef,
}: UseSectionDragAnimationParams) {
  // Track whether we've applied initial counter-transforms (to avoid re-applying on re-renders)
  const initializedRef = useRef(false)
  // Store base counter-offsets so swap transforms can build on top
  const baseOffsetsRef = useRef<Map<number, number>>(new Map())
  // Previous activeSectionDragId — detect transitions
  const prevDragIdRef = useRef<string | null>(null)
  // Transitions are disabled for the first frame after drag start to prevent
  // flicker from closestCenter collision detection settling on the nearest zone.
  const transitionsEnabledRef = useRef(false)

  // ── Algebraic position refs (populated in Phase A, consumed in Phase B) ──
  // Segment DOM positions AFTER CSS changes (before counter-transforms).
  const segDomInfoRef = useRef<Map<number, { top: number; height: number }>>(new Map())
  // Drop zone DOM top positions AFTER CSS changes (before counter-transforms).
  const dzDomTopsRef = useRef<Map<number, number>>(new Map())
  // Which DZs are inactive (0 height — disabled or unsectioned).
  const dzInactiveRef = useRef<Set<number>>(new Set())

  /**
   * Find the reference segment index for a drop zone's Phase A transform.
   * Each drop zone moves with the segment that follows it (or the last segment
   * for the trailing drop zone). If the reference is the source, use the nearest
   * non-source segment instead.
   */
  const getDropZoneRefIdx = (dzIdx: number, sourceIdx: number, segCount: number): number | null => {
    let refIdx = dzIdx < segCount ? dzIdx : segCount - 1
    if (refIdx === sourceIdx) {
      refIdx = sourceIdx < segCount - 1 ? sourceIdx + 1 : sourceIdx - 1
    }
    if (refIdx < 0 || refIdx >= segCount) return null
    return refIdx
  }

  // ── Phase A: Counter-transforms on drag start ──
  // Fires after React commits CSS changes (drop zones expand, margins collapse).
  // Reads new positions, compares with snapshots, applies instant counter-transforms.
  // Also snapshots DOM positions for Phase B's algebraic computation.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    // Drag just started — apply counter-transforms synchronously (before paint)
    if (activeSectionDragId && !prevDragIdRef.current) {
      initializedRef.current = false
      const sourceIdx = segments.findIndex(s => s.section?.id === activeSectionDragId)

      // How much taller the full segment is compared to just the header.
      const sourceRect = segmentRectsRef.current.get(sourceIdx)
      const fullHeight = sourceRect ? sourceRect.height : 0
      const hdrHeight = sourceHeaderHeightRef.current || fullHeight
      const taskOverflow = fullHeight - hdrHeight

      const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')
      baseOffsetsRef.current.clear()
      segDomInfoRef.current.clear()

      segEls.forEach((el) => {
        const idx = parseInt(el.getAttribute('data-segment-idx')!, 10)
        const oldRect = segmentRectsRef.current.get(idx)
        if (!oldRect) return

        // getBoundingClientRect() forces layout recalc with new CSS classes applied.
        const newRect = el.getBoundingClientRect()

        // Store DOM position for Phase B algebraic computation
        segDomInfoRef.current.set(idx, { top: newRect.top, height: newRect.height })

        if (idx === sourceIdx) return // Source is collapsing, skip counter-transform

        let deltaY = oldRect.top - newRect.top

        // Segments below the source: reduce counter-transform so the gap
        // at the source position is only header-sized, not full-segment-sized.
        if (idx > sourceIdx && taskOverflow > 0) {
          deltaY -= taskOverflow
        }

        baseOffsetsRef.current.set(idx, deltaY)

        // Apply counter-transform immediately (no transition)
        el.style.transition = 'none'
        el.style.transform = deltaY !== 0 ? `translateY(${deltaY}px)` : ''
      })

      // Snapshot drop zone DOM positions and apply counter-transforms
      const dzEls = wrapper.querySelectorAll<HTMLElement>('[data-drop-zone-idx]')
      dzDomTopsRef.current.clear()
      dzInactiveRef.current.clear()

      dzEls.forEach((el) => {
        const dzIdx = parseInt(el.getAttribute('data-drop-zone-idx')!, 10)

        if (el.offsetHeight === 0) {
          dzInactiveRef.current.add(dzIdx)
          return
        }

        // Store DOM top for Phase B
        dzDomTopsRef.current.set(dzIdx, el.getBoundingClientRect().top)

        // Counter-transforms only for DZs below the source
        if (dzIdx <= sourceIdx) return

        const refIdx = getDropZoneRefIdx(dzIdx, sourceIdx, segments.length)
        if (refIdx === null) return

        const offset = baseOffsetsRef.current.get(refIdx) ?? 0
        el.style.transition = 'none'
        el.style.transform = offset !== 0 ? `translateY(${offset}px)` : ''
      })

      initializedRef.current = true
      transitionsEnabledRef.current = false
      requestAnimationFrame(() => { transitionsEnabledRef.current = true })
    }

    // Drag just ended — clear all transforms
    if (!activeSectionDragId && prevDragIdRef.current) {
      const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')
      segEls.forEach((el) => {
        el.style.transform = ''
        el.style.transition = ''
      })
      const dzEls = wrapper.querySelectorAll<HTMLElement>('[data-drop-zone-idx]')
      dzEls.forEach((el) => {
        el.style.transform = ''
        el.style.transition = ''
      })
      initializedRef.current = false
      baseOffsetsRef.current.clear()
      segDomInfoRef.current.clear()
      dzDomTopsRef.current.clear()
      dzInactiveRef.current.clear()
    }

    prevDragIdRef.current = activeSectionDragId
  }, [activeSectionDragId, segments, wrapperRef, segmentRectsRef, sourceHeaderHeightRef])

  // ── Phase B: Swap transforms during drag ──
  // All positions computed ALGEBRAICALLY from Phase A snapshots — no DOM reads.
  useEffect(() => {
    if (!activeSectionDragId || !initializedRef.current) return

    const wrapper = wrapperRef.current
    if (!wrapper) return

    const sourceIdx = segments.findIndex(s => s.section?.id === activeSectionDragId)
    if (sourceIdx === -1) return

    const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')

    // Use section header height for shift calculations.
    const sourceRect = segmentRectsRef.current.get(sourceIdx)
    const headerHeight = sourceHeaderHeightRef.current || (sourceRect ? sourceRect.height : 0)

    // Compute shift amounts: header height + inter-segment margin from snapshots.
    let shiftDown = headerHeight
    let shiftUp = headerHeight
    if (sourceRect) {
      const nextRect = segmentRectsRef.current.get(sourceIdx + 1)
      if (nextRect) {
        const margin = nextRect.top - sourceRect.top - sourceRect.height
        shiftDown = headerHeight + Math.max(margin, 0)
      }
      const prevRect = segmentRectsRef.current.get(sourceIdx - 1)
      if (prevRect) {
        const margin = sourceRect.top - prevRect.top - prevRect.height
        shiftUp = headerHeight + Math.max(margin, 0)
      }
    }

    // Helper: compute swap offset for a given segment index
    const getSwapOffset = (idx: number): number => {
      if (hoverTargetIdx === null) return 0
      if (hoverTargetIdx > sourceIdx + 1) {
        if (idx > sourceIdx && idx < hoverTargetIdx) return -shiftDown
      } else if (hoverTargetIdx < sourceIdx) {
        if (idx >= hoverTargetIdx && idx < sourceIdx) return shiftUp
      }
      return 0
    }

    const transitionStyle = transitionsEnabledRef.current
      ? 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)'
      : 'none'

    // ── Apply segment transforms ──
    segEls.forEach((el) => {
      const idx = parseInt(el.getAttribute('data-segment-idx')!, 10)
      if (idx === sourceIdx) return

      const baseOffset = baseOffsetsRef.current.get(idx) ?? 0
      const totalOffset = baseOffset + getSwapOffset(idx)

      el.style.transition = transitionStyle
      el.style.transform = totalOffset !== 0 ? `translateY(${totalOffset}px)` : ''
    })

    // ── Compute segment visual positions ALGEBRAICALLY ──
    // segVisualTop = segDomTop + baseOffset + swapOffset
    const segVisual = new Map<number, { top: number; bottom: number }>()
    for (const [idx, info] of segDomInfoRef.current) {
      if (idx === sourceIdx) continue
      const totalOffset = (baseOffsetsRef.current.get(idx) ?? 0) + getSwapOffset(idx)
      const top = info.top + totalOffset
      segVisual.set(idx, { top, bottom: top + info.height })
    }

    // ── Position drop zones ──
    const dzEls = wrapper.querySelectorAll<HTMLElement>('[data-drop-zone-idx]')

    // No hover: keep Phase A counter-transforms (DZs track their reference segment)
    if (hoverTargetIdx === null) {
      dzEls.forEach((el) => {
        const dzIdx = parseInt(el.getAttribute('data-drop-zone-idx')!, 10)
        if (dzInactiveRef.current.has(dzIdx)) return
        if (dzIdx > sourceIdx) {
          const refIdx = getDropZoneRefIdx(dzIdx, sourceIdx, segments.length)
          if (refIdx !== null) {
            const offset = baseOffsetsRef.current.get(refIdx) ?? 0
            el.style.transition = transitionStyle
            el.style.transform = offset !== 0 ? `translateY(${offset}px)` : ''
            return
          }
        }
        el.style.transition = transitionStyle
        el.style.transform = ''
      })
      return
    }

    // With hover: position each DZ between its neighboring segments
    dzEls.forEach((el) => {
      const dzIdx = parseInt(el.getAttribute('data-drop-zone-idx')!, 10)

      // Skip inactive DZs (disabled or 0 height)
      if (dzInactiveRef.current.has(dzIdx)) {
        el.style.transition = transitionStyle
        el.style.transform = ''
        return
      }

      // DZ natural center: DOM position from Phase A snapshot
      const dzDomTop = dzDomTopsRef.current.get(dzIdx)
      if (dzDomTop === undefined) return
      const naturalCenter = dzDomTop + DZ_HEIGHT / 2

      // Find nearest non-source segment above and below this DZ
      let aboveIdx: number | null = null
      for (let j = dzIdx - 1; j >= 0; j--) {
        if (j !== sourceIdx && segVisual.has(j)) { aboveIdx = j; break }
      }
      let belowIdx: number | null = null
      for (let j = dzIdx; j < segments.length; j++) {
        if (j !== sourceIdx && segVisual.has(j)) { belowIdx = j; break }
      }

      let desiredCenter: number
      if (aboveIdx !== null && belowIdx !== null) {
        // Between two non-source segments: center in the gap
        desiredCenter = (segVisual.get(aboveIdx)!.bottom + segVisual.get(belowIdx)!.top) / 2
      } else if (belowIdx !== null) {
        // Topmost edge
        if (dzIdx === hoverTargetIdx) {
          // Active hover: ensure SECTION_GAP between DZ bottom and segment top
          desiredCenter = segVisual.get(belowIdx)!.top - DZ_HEIGHT / 2 - SECTION_GAP
        } else {
          // Inactive: flush above segment
          desiredCenter = segVisual.get(belowIdx)!.top - DZ_HEIGHT / 2
        }
      } else if (aboveIdx !== null) {
        // Trailing edge. Two sub-cases:
        // 1) Actual trailing DZ (after last segment): collapsed sections already
        //    include AddTask spacing, so skip SECTION_GAP for them.
        // 2) DZ that's "trailing" because the source is below it: always needs gap.
        const isRealTrailingDZ = dzIdx === segments.length
        const skipGap = isRealTrailingDZ && segments[aboveIdx]?.tasks.length === 0
        if (dzIdx === hoverTargetIdx && !skipGap) {
          desiredCenter = segVisual.get(aboveIdx)!.bottom + DZ_HEIGHT / 2 + SECTION_GAP
        } else {
          desiredCenter = segVisual.get(aboveIdx)!.bottom + DZ_HEIGHT / 2
        }
      } else {
        el.style.transition = transitionStyle
        el.style.transform = ''
        return
      }

      const offset = desiredCenter - naturalCenter
      el.style.transition = transitionStyle
      el.style.transform = Math.abs(offset) > 0.5 ? `translateY(${offset}px)` : ''
    })
  }, [activeSectionDragId, hoverTargetIdx, segments, wrapperRef, segmentRectsRef, sourceHeaderHeightRef])

  // ── Phase C: FLIP settle on drop ──
  useLayoutEffect(() => {
    const anim = dropAnimRef.current
    if (!anim) return
    if (activeSectionDragId) return // Only run after drag ends
    dropAnimRef.current = null

    const wrapper = wrapperRef.current
    if (!wrapper) return

    // Clear all transforms (elements are at correct DOM positions now)
    const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')
    segEls.forEach((el) => {
      el.style.transform = ''
      el.style.transition = ''
    })
    const dzEls = wrapper.querySelectorAll<HTMLElement>('[data-drop-zone-idx]')
    dzEls.forEach((el) => {
      el.style.transform = ''
      el.style.transition = ''
    })

    // Find the dropped section's header
    const targetEl = wrapper.querySelector<HTMLElement>(`.section-header[data-section-id="${anim.sectionId}"]`)
    if (!targetEl) return

    const finalRect = targetEl.getBoundingClientRect()
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
    // WAAPI magnetic settle: overlay position → final DOM position, popping on the way.
    const { motion, shadow, duration } = dropPopMotion({ dx: anim.x - finalRect.left, dy: anim.y - finalRect.top, reduced })
    const timing: KeyframeAnimationOptions = { duration, easing: 'linear', fill: 'none' }
    // Header and its tasks land together (Sven, 03.10.2026). Only the sortable rows —
    // an exiting row also carries `data-task-id` and must not move twice.
    const segment = targetEl.closest<HTMLElement>('[data-segment-idx]')
    const rows = segment ? Array.from(segment.querySelectorAll<HTMLElement>('.sortable-task-entry')) : []
    targetEl.animate(motion, timing)
    targetEl.animate(shadow, timing)
    for (const row of rows) {
      row.animate(motion, timing)
      // Every row with its own shadow looked restless (Sven, 03.10.2026) — only selected
      // tasks carry one, on their pill (`.task-entry`), like a dropped task does.
      const pill = row.querySelector<HTMLElement>('.task-entry--selected')
      pill?.animate(shadow, timing)
    }
  }, [activeSectionDragId, wrapperRef, dropAnimRef])
}
